import i18nFactory from '@yandex-int/i18n';

import { ApiMethodExecutingError } from '../../service/Errors';
import { formatNumber, getNumberValue } from '../../service/FormatHelpers/FormatHelpers';
import { ValidationResultItem } from '../../typings/common';
import { ValidationErrorType, ValidationResult } from '../../typings/common';
import { CurrencyCode, SwitchOffLimitPaymentData_client_autoOverdraftPaymentOptions } from '../../typings/graphql-grid';
import { PayerData, PaymentMethodsData } from '../../typings/walletState';
import { mapStringToCurrencySymbol } from '../common/common';

import * as keyset from './wallet.i18n';

const i18n = i18nFactory(keyset);

export function getPaymentMethodByCode(code: string, name: string): string {
    switch (code) {
        case 'bank':
            return i18n('bank-payment');
        case 'card':
            return i18n('card-payment');
        case 'yamoney_wallet':
            return i18n('yamoney-wallet-payment');
        case 'webmoney_wallet':
            return i18n('webmoney-wallet-payment');
        case 'paypal_wallet':
            return i18n('paypal-wallet-payment');
        default:
            return name;
    }
}

export function mapSwitchOffLimitPaymentDataFromApiFormat(
    autoOverdraftPaymentOptions: SwitchOffLimitPaymentData_client_autoOverdraftPaymentOptions[]
): {
    payerData: Array<PayerData>;
    paymentMethods: Record<string, Array<PaymentMethodsData>>;
    selectedPayerId: string;
    selectedPaymentMethodId: string;
} {
    return autoOverdraftPaymentOptions.reduce(
        (
            acc: {
                payerData: Array<PayerData>;
                paymentMethods: Record<string, Array<PaymentMethodsData>>;
                selectedPayerId: string;
                selectedPaymentMethodId: string;
            },
            option
        ) => {
            const payerId = String(option.personInfo.id);

            acc.payerData.push({
                id: payerId,
                name: option.personInfo.name
            });

            acc.paymentMethods[payerId] = option.paymentMethods.map(({ code: id, name, limit }) => ({
                id: String(id),
                name: getPaymentMethodByCode(id, name),
                limit: getNumberValue(limit)
            }));

            if (option.personInfo.selected) {
                acc.selectedPayerId = payerId;

                const selectedPaymentMethodData = option.paymentMethods.find(method => method.selected);

                acc.selectedPaymentMethodId = (selectedPaymentMethodData as {
                    code: string;
                    name: string;
                }).code;
            }

            return acc;
        },
        {
            payerData: [],
            paymentMethods: {},
            selectedPayerId: '',
            selectedPaymentMethodId: ''
        }
    );
}

export function getValidationResultFromApiError(
    error: ApiMethodExecutingError,
    currency: CurrencyCode,
    paymentMethodName: string = ''
): ValidationResult {
    const errors = error.getErrors();
    const apiPathToClient = {
        paymentMethodCode: 'paymentMethodId',
        personId: 'payerId',
        autoOverdraftLimit: 'sum'
    };
    const getErrorText = (validationResult: ValidationResultItem): string => {
        switch (validationResult.code) {
            case 'AutoOverdraftsDefects.VoidDefects.BALANCE_UNAVAILABLE_EXCEPTION':
                return i18n('balance-unavailable-exception');

            case 'DefectIds.CANNOT_BE_NULL':
                return i18n('cannot-be-null');

            case 'AutoOverdraftsDefects.VoidDefects.CLIENT_MUST_HAVE_PERSONAL_WALLET':
                return i18n('no-rights-for-edit-wallet');

            case 'AutoOverdraftsDefects.VoidDefects.CLIENT_MUST_NOT_BE_BRAND':
                return i18n('no-rights-for-edit-auto-overdraft');

            case 'AutoOverdraftsDefects.VoidDefects.MONEY_ON_WALLET_MUST_BE_GREATER_THAN_ZERO':
                return i18n('wallet-sum-must-be-not-null');

            case 'AutoOverdraftsDefects.VoidDefects.PERSONAL_WALLET_CURRENCY_MUST_NOT_BE_YND_FIXED':
                return i18n('ynd-fixed-currency-error');

            case 'NumberDefectIds.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX':
                return i18n('too-big-payment', {
                    maxSum: formatNumber(
                        (validationResult.params as {
                            min: number;
                            max: number;
                        }).max,
                        2
                    ),
                    currency: mapStringToCurrencySymbol(currency),
                    paymentMethodName: paymentMethodName
                });

            case 'NumberDefectIds.MUST_BE_IN_THE_INTERVAL_INCLUSIVE':
                return i18n('auto-overdraft-sum-interval', {
                    minSum: formatNumber(
                        (validationResult.params as {
                            min: number;
                            max: number;
                        }).min,
                        2
                    ),
                    maxSum: formatNumber(
                        (validationResult.params as {
                            min: number;
                            max: number;
                        }).max,
                        2
                    ),
                    currency: mapStringToCurrencySymbol(currency)
                });

            default:
                return i18n('general-error');
        }
    };

    return {
        valid: false,
        errors: errors.map((validationResult: ValidationResultItem) => {
            return {
                type: ValidationErrorType.server,
                text: getErrorText(validationResult),
                path: apiPathToClient[validationResult.path] || 'general'
            };
        })
    };
}
